/**
 * Novidades (spec 036) — o modelo PURO: tipos das entradas, quem vê o quê (D3),
 * o contador, os agrupamentos por dia e por mês e o texto com os termos do
 * tenant. Sem React e sem alias `@/`, de propósito: a guarda das entradas
 * (apps/api/src/shared/news-entries.test.ts) importa este arquivo e o
 * entries.ts, como a guarda de navegação importa o simple-nav.ts — o web não
 * tem executor de testes.
 */
import type { NewsCapability, PermissionKey, ResourceSource, Terms } from '@erp/shared';

// ── as entradas ─────────────────────────────────────────────────────────────

/** equipe (o app interno) ou portal do cliente */
export type NewsAudience = 'staff' | 'portal';
export const NEWS_AUDIENCES: readonly NewsAudience[] = ['staff', 'portal'];

export type NewsRole = 'admin' | 'attendant' | 'client';

/**
 * O que a pessoa e o tenant precisam ter para a novidade aparecer (D3). Tudo
 * o que é declarado precisa valer; o que não é declarado não restringe.
 */
export interface NewsRequirements {
  /** a pessoa tem UM destes papéis */
  roles?: readonly NewsRole[];
  /** a pessoa tem ao menos UMA destas permissões (me.permissions; admin tem todas) */
  anyPermission?: readonly PermissionKey[];
  /** o tenant tem TODAS estas (calculadas na API: GET /news/state) */
  capabilities?: readonly NewsCapability[];
  /** de onde vem o recurso no perfil do tenant (spec 017) — ex.: a ficha só existe com `ticket_identifier` */
  resourceSources?: readonly ResourceSource[];
}

/**
 * Texto com os termos do tenant: `{contact}` vira "associado" num tenant e
 * "contato" no outro; `{Contact}` (maiúscula) capitaliza. A guarda de
 * vocabulário da spec 017 vale aqui: palavra de vertical nunca vai escrita.
 * `**negrito**` marca o que se clica.
 */
export type NewsText = string;

export type NewsBlock =
  | { kind: 'p'; text: NewsText }
  | { kind: 'list'; items: readonly NewsText[] };

/** imagem em apps/web/public/novidades/, só de dados de demonstração (D5) */
export interface NewsImage {
  src: string;
  alt: NewsText;
}

/** ícones do balão do tour — o nome é do lucide, resolvido no componente */
export const NEWS_TOUR_ICONS = ['search', 'filter', 'key', 'users', 'map', 'list', 'file', 'sparkles', 'refresh', 'bell'] as const;
export type NewsTourIcon = (typeof NEWS_TOUR_ICONS)[number];

export interface NewsTourStep {
  /** `data-tour="<anchor>"` num componente da tela; sem a âncora na tela, o passo é pulado (D4) */
  anchor: string;
  title: NewsText;
  text: NewsText;
  icon?: NewsTourIcon;
}

export interface NewsTour {
  id: string;
  steps: readonly NewsTourStep[];
}

export interface NewsAction {
  label: string;
  /** rota do SPA (uma das do router) */
  to: string;
  search?: Readonly<Record<string, string | number>>;
  /** ao chegar, começa o tour da novidade */
  startsTour?: boolean;
}

export interface NewsEntry {
  /** estável para sempre: é o que a API guarda como "visto" */
  id: string;
  /** `AAAA-MM-DD`, o dia em que a mudança foi para o ar */
  date: string;
  title: NewsText;
  /** 2 ou 3 linhas no cartão do painel */
  summary: NewsText;
  /** o texto inteiro (página do histórico e "Ler mais"): o antes, o depois e onde clicar */
  body: readonly NewsBlock[];
  images?: readonly NewsImage[];
  audience: NewsAudience;
  requires?: NewsRequirements;
  action?: NewsAction;
  tour?: NewsTour;
}

// ── quem vê (D3) ────────────────────────────────────────────────────────────

export interface NewsViewer {
  audience: NewsAudience;
  role: NewsRole | null;
  can: (key: PermissionKey) => boolean;
  capabilities: Readonly<Record<NewsCapability, boolean>>;
  resourceSource: ResourceSource;
}

export function isVisibleTo(entry: NewsEntry, viewer: NewsViewer): boolean {
  if (entry.audience !== viewer.audience) return false;
  const r = entry.requires;
  if (!r) return true;
  if (r.roles && (viewer.role === null || !r.roles.includes(viewer.role))) return false;
  if (r.anyPermission && !r.anyPermission.some((k) => viewer.can(k))) return false;
  if (r.capabilities && !r.capabilities.every((c) => viewer.capabilities[c])) return false;
  if (r.resourceSources && !r.resourceSources.includes(viewer.resourceSource)) return false;
  return true;
}

export function visibleEntries(entries: readonly NewsEntry[], viewer: NewsViewer): NewsEntry[] {
  return entries.filter((e) => isVisibleTo(e, viewer));
}

// ── o contador ──────────────────────────────────────────────────────────────

/**
 * Até quantos dias uma novidade não vista acende o contador. Quem entra na
 * equipe daqui a um ano não começa com 40 no megafone: o histórico inteiro
 * continua na página, mas o número é do que é recente.
 */
export const NEWS_UNSEEN_WINDOW_DAYS = 90;

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `AAAA-MM-DD` que existe no calendário (2026-02-30 não) */
export function parseNewsDate(date: string): { y: number; m: number; d: number } | null {
  const match = DATE_RE.exec(date);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== m - 1 || probe.getUTCDate() !== d) return null;
  return { y, m, d };
}

/** dias corridos de `date` até `today` (os dois `AAAA-MM-DD`) */
export function daysBetween(date: string, today: string): number {
  const a = parseNewsDate(date);
  const b = parseNewsDate(today);
  if (!a || !b) return Number.POSITIVE_INFINITY;
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / 86_400_000);
}

/** hoje no fuso de São Paulo, `AAAA-MM-DD` — o dia da operação, não o do UTC */
export function todayInSaoPaulo(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

export function unseenEntries(
  visible: readonly NewsEntry[],
  seenIds: readonly string[],
  today: string,
  windowDays: number = NEWS_UNSEEN_WINDOW_DAYS
): NewsEntry[] {
  const seen = new Set(seenIds);
  return visible.filter((e) => !seen.has(e.id) && daysBetween(e.date, today) <= windowDays);
}

// ── agrupamentos ────────────────────────────────────────────────────────────

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** "15 de setembro" — o cabeçalho do painel */
export function dayHeading(date: string): string {
  const p = parseNewsDate(date);
  return p ? `${p.d} de ${MONTHS[p.m - 1]}` : date;
}

/** "Outubro de 2026" — o cabeçalho da página do histórico */
export function monthHeading(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const name = MONTHS[(m ?? 1) - 1] ?? '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} de ${y}`;
}

export interface NewsGroup {
  key: string;
  heading: string;
  entries: NewsEntry[];
}

/** na ordem em que vieram (o catálogo já é do mais novo para o mais antigo) */
function groupBy(entries: readonly NewsEntry[], keyOf: (e: NewsEntry) => string, heading: (k: string) => string) {
  const groups: NewsGroup[] = [];
  for (const entry of entries) {
    const key = keyOf(entry);
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.entries.push(entry);
    else groups.push({ key, heading: heading(key), entries: [entry] });
  }
  return groups;
}

export const groupByDay = (entries: readonly NewsEntry[]) => groupBy(entries, (e) => e.date, dayHeading);
export const groupByMonth = (entries: readonly NewsEntry[]) =>
  groupBy(entries, (e) => e.date.slice(0, 7), monthHeading);

/** âncora da entrada na página do histórico (`/novidades#<id>`) */
export const newsAnchorId = (id: string) => `novidade-${id}`;

// ── o texto com os termos do tenant ─────────────────────────────────────────

/**
 * Os tokens que o texto pode usar. `source` é o nome do sistema de origem do
 * recurso, que vem da API (o SPA não o escreve — guarda de vocabulário).
 */
export const NEWS_TEXT_TOKENS = [
  'ticket',
  'tickets',
  'client',
  'clients',
  'contact',
  'contacts',
  'resource',
  'resources',
  'resourceId',
  'desk',
  'desks',
  'source',
] as const;
export type NewsTextToken = (typeof NEWS_TEXT_TOKENS)[number];
export type NewsTextVars = Record<NewsTextToken, string>;

export const TOKEN_RE = /\{([A-Za-z]+)\}/g;

export function newsTextVars(terms: Terms, sourceName: string): NewsTextVars {
  return {
    ticket: terms.ticket,
    tickets: terms.tickets,
    client: terms.client,
    clients: terms.clients,
    contact: terms.contact,
    contacts: terms.contacts,
    resource: terms.resource,
    resources: terms.resources,
    resourceId: terms.resourceId,
    desk: terms.desk,
    desks: terms.desks,
    // sem o nome (portal, ou a consulta ainda não voltou): uma frase neutra
    source: sourceName.trim() || 'sistema de origem',
  };
}

/** `{contact}` → termo; `{Contact}` → Termo; token desconhecido fica como está (a guarda reprova) */
export function renderNewsText(text: NewsText, vars: NewsTextVars): string {
  return text.replace(TOKEN_RE, (whole, raw: string) => {
    const lower = `${raw.charAt(0).toLowerCase()}${raw.slice(1)}`;
    if (!(NEWS_TEXT_TOKENS as readonly string[]).includes(lower)) return whole;
    const value = vars[lower as NewsTextToken];
    return raw.charAt(0) === raw.charAt(0).toUpperCase() ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
  });
}

/** `**negrito**` em pedaços: os ímpares são negrito */
export function splitBold(text: string): string[] {
  return text.split('**');
}

/** todo texto de uma entrada (para a guarda dos tokens e do vocabulário) */
export function entryTexts(entry: NewsEntry): string[] {
  const texts = [entry.title, entry.summary];
  for (const b of entry.body) {
    if (b.kind === 'p') texts.push(b.text);
    else texts.push(...b.items);
  }
  for (const img of entry.images ?? []) texts.push(img.alt);
  for (const step of entry.tour?.steps ?? []) texts.push(step.title, step.text);
  if (entry.action) texts.push(entry.action.label);
  return texts;
}

// ── o tour (D4) ─────────────────────────────────────────────────────────────

/**
 * O próximo passo que TEM âncora na tela, a partir de `from` (inclusive). Passo
 * sem âncora é pulado; null = acabaram os passos com âncora.
 */
export function nextShownStep(
  steps: readonly NewsTourStep[],
  from: number,
  anchorExists: (anchor: string) => boolean
): number | null {
  for (let i = Math.max(0, from); i < steps.length; i++) {
    if (anchorExists(steps[i]!.anchor)) return i;
  }
  return null;
}
